import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getUser as getGeneralUser, switchTenant } from "../../../actions/Auth/actions";
import { ConfigProvider } from "../../../actions/Tenants/config/configHook";
import { scopes } from "../../../authConfig";
import { IS_DEMO } from "../../../constants/urls";
import { OfflineReplayDriver } from "../../../offline/OfflineReplayDriver";
import { OfflineServiceWorker } from "../../../offline/OfflineServiceWorker";
import {
    OfflineStorageWarning,
    OfflineUnloadGuard,
} from "../../../offline/OfflineStorageGuards";
import { OfflineTenantInvalidator } from "../../../offline/OfflineTenantInvalidator";
import { toast } from "../../../utils/toast";
import { Loading } from "../../Helper/Loading";
import DefaultLayout from "../Layout/DefaultLayout";
import UserNotAuthorized from "./UserNotAuthorized";

interface RequireAuthPropsType {
    element: React.ReactNode;
    header?: React.ReactNode;
    fullscreen?: boolean;
    bgWhite?: boolean;
    disableRefresh?: boolean;
    getUser?: (data?: any) => void;
    /** When true, reads :tenant_id from the URL and verifies the user is authorized
     *  to access that tenant. Redirects or silently impersonates as needed. */
    prefixTenant?: boolean;
}

export function RequireAuth({ element, fullscreen = false, bgWhite, disableRefresh = false, header, getUser = getGeneralUser, prefixTenant = false }: RequireAuthPropsType) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [authCheckComplete, setAuthCheckComplete] = useState(false); // Track whether auth check is done
    // Tenant check states — skipped entirely when prefixTenant=false
    const [tenantCheckComplete, setTenantCheckComplete] = useState(!prefixTenant);
    const [tenantCheckFailed, setTenantCheckFailed] = useState(false);
    const { authenticated, isLoading } = useSelector((state: any) => state.auth);
    const user = useSelector((state: any) => state.auth.user);
    const { instance } = useMsal();
    const account = instance.getActiveAccount();

    const accessTokenRequest = {
        scopes,
        account,
        redirectUri: "/blank.html",
    };

    // Replace, don't push: the login round-trip must not leave /login sitting in
    // browser history behind the page. Otherwise pressing Back later lands on the
    // login screen. redirect_to (carried in the URL + localStorage) survives the
    // replace, so the post-login destination is preserved.
    const redirectToLogin = () => navigate(`/login?redirect_to=${location.pathname}${location.search}`, { replace: true });

    useEffect(() => {
        // AUTH scheme is overwritten in demo
        if (IS_DEMO) {
            dispatch(getUser() as any);
            setAuthCheckComplete(true); // Mark as complete in demo mode
            return;
        }

        if (account) {
            // Attempt to refresh token
            instance.acquireTokenSilent(accessTokenRequest as any).then((response) => {
                if (response) {
                    dispatch(getUser() as any);
                }
                setAuthCheckComplete(true); // Auth check complete
            }).catch((error) => {
                console.error(error); // eslint-disable-line no-console
                redirectToLogin();
                setAuthCheckComplete(true); // Complete even on error
            });
        } else {
            redirectToLogin();
            setAuthCheckComplete(true); // Complete if no account
        }
    }, [
        account?.homeAccountId, // * As soon as MS account id is available, we can check for auth against our own backend to fetch user & tenant info
        instance,
        IS_DEMO
    ]); // Prevent infinite loop by referring to homeAccountId

    // Tenant authorization check — only runs when prefixTenant=true and user is authenticated
    useEffect(() => {
        if (!prefixTenant || !authenticated || !user?.tenant?.tenant_id) return;

        const urlTenantId = params.tenant_id;

        // No tenant_id in URL or already matches current session → no switch needed
        if (!urlTenantId || urlTenantId === user.tenant.tenant_id) {
            setTenantCheckComplete(true);
            setTenantCheckFailed(false);
            return;
        }

        // Mismatch — reset check state before evaluating
        setTenantCheckComplete(false);
        setTenantCheckFailed(false);

        // Authorization: must be global admin OR a member of the target tenant
        const isAuthorized = user.is_admin || user.tenants?.some((tu: any) => tu.tenant_id === urlTenantId);

        if (!isAuthorized) {
            setTenantCheckFailed(true);
            return;
        }

        // Authorized — silently switch to the target tenant
        const targetTenantUser = user.tenants?.find((tu: any) => tu.tenant_id === urlTenantId)
            || { tenant_id: urlTenantId };

        dispatch(switchTenant(targetTenantUser) as any).then(() => {
            toast.info(`Switched to ${targetTenantUser.tenant_name || urlTenantId}`, { toastId: "tenant-switch" });
            setTenantCheckComplete(true);
        });
    }, [authenticated, params.tenant_id, user?.tenant?.tenant_id]);


    // Render child component or fallback based on authentication state
    const getChildComponent = () => {
        if (!authenticated && (!authCheckComplete || isLoading)) {
            // Show loading while auth check is in progress and we are not authenticated yet (so not for every refresh)
            return <Loading />;
        }
        if (authCheckComplete && ((!account && !IS_DEMO) || !authenticated)) {
            // Show UserNotAuthorized only after auth check is complete and is not loading
            return <UserNotAuthorized />;
        }

        // Hold rendering until tenant authorization is resolved
        if (prefixTenant && !tenantCheckComplete) {
            return tenantCheckFailed ? <UserNotAuthorized /> : <Loading />;
        }

        return element;
    };

    return (
        <ConfigProvider refreshInterval={disableRefresh ? false : 6 * 60 * 1000}>
            <>
                <OfflineServiceWorker />
                <OfflineTenantInvalidator />
                <OfflineStorageWarning />
                <OfflineUnloadGuard />
                <OfflineReplayDriver />
                <DefaultLayout
                    bgWhite={bgWhite}
                    fullscreen={fullscreen}
                    header={fullscreen ? undefined : header}
                >
                    {getChildComponent()}
                </DefaultLayout>
            </>
        </ConfigProvider>
    );
}
